// App.js
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import PrivacyPage from './PrivacyPage';
import TermsPage from './TermsPage';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (totalScroll / windowHeight) * 100;
      setScrollProgress(progress);
    };

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.section, .feature-card, .step-card, .value-card').forEach(el => {
      observer.observe(el);
    });

    window.addEventListener('scroll', handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const features = [
    {
      icon: "🤝",
      title: "Smart Employer Allocation",
      description: "Automatically match employers with the right household staff based on skills and needs.",
    },
    {
      icon: "🔗",
      title: "Employer–Employee Connections",
      description: "Easily connect and manage trusted relationships between households and staff.",
    },
    {
      icon: "🛡️",
      title: "Auto Identity Verification",
      description: "Built-in ID verification ensures safety and trust for both employers and employees.",
    },
    {
      icon: "💬",
      title: "Instant Messaging",
      description: "Communicate in real time with secure in-app messaging between employers and staff.",
    },
    {
      icon: "👥",
      title: "Staff & Household Profiles",
      description: "Manage staff information, roles, and household details all in one secure platform.",
    },
    {
      icon: "💰",
      title: "Wallet & Payments",
      description: "Enable secure in-app wallet transfers and manage payments seamlessly.",
    },
    {
      icon: "🔔",
      title: "Instant Notifications",
      description: "Stay updated with real-time alerts for schedules, payments, and profile changes.",
    },
  ];
  
  const steps = [
    {
      number: "01",
      title: "Download & Register",
      description: "Get Home Manager from the app store, create your account, and verify your identity for safety.",
      icon: "📥"
    },
    {
      number: "02",
      title: "Search & Discover",
      description: "Use the home screen to search for employers or employees that fit your needs.",
      icon: "🔍"
    },
    {
      number: "03",
      title: "Send a Request",
      description: "Visit an employer or employee profile, send a request, and pay a small finder’s fee.",
      icon: "📤"
    },
    {
      number: "04",
      title: "Manage Requests",
      description: "View your sent and received requests. Accept or reject based on your preferences.",
      icon: "📩"
    },
    {
      number: "05",
      title: "Start Chatting",
      description: "Once the finder’s fee is paid and the request accepted, unlock instant messaging with the person.",
      icon: "💬"
    },
    {
      number: "06",
      title: "Profiles & Verification",
      description: "Set up or review household/staff profiles with auto ID verification for trust and transparency.",
      icon: "🛡️"
    },
    {
      number: "07",
      title: "Wallet & Payments",
      description: "Securely handle payments through in-app wallets and keep track of all transactions.",
      icon: "💰"
    },
    {
      number: "08",
      title: "Stay Updated",
      description: "Get real-time notifications for new requests, accepted offers, payments, and chats.",
      icon: "🔔"
    }
  ];
  
  const values = [
    {
      icon: "⚡",
      title: "Simplicity",
      description: "We believe home management should be effortless and intuitive for everyone.",
    },
    {
      icon: "🛡️",
      title: "Reliability",
      description: "Count on us for consistent performance and dependable service you can trust.",
    },
    {
      icon: "🔐",
      title: "Security",
      description: "Your data's security is our top priority with enterprise-grade protection.",
    },
    {
      icon: "🌍",
      title: "Global Standards",
      description: "Designed to meet international household management standards and practices.",
    }
  ];

  const screenshots = [
    {
      id: 1,
      title: "Login",
      description: "User login with email and password to have quick access to essential features.",
      image: "/screens/login.PNG",
      thumbnail: "/screens/login.PNG",
      features: ["login", "forgotpassword","Signup"]
    },
    {
      id: 2,
      title: "Signup",
      description: "Create account by providing details about you.",
      image: "/screens/signup.PNG",
      thumbnail: "/screens/signup.PNG",
      features: ["Add Email and password", "Upload your Photos", "Provide what You Need", "Add Bio data","Provide Accurate Data"]
    },
    {
      id: 3,
      title: "Home Screen",
      description: "Displays employees and employers.",
      image: "/screens/home1.PNG",
      thumbnail: "/screens/home1.PNG",
      features: ["View Profile", "Rate user", "Report Issue", "View wallet",""]
    },
    {
      id: 4,
      title: "Profile",
      description: "Shows User Profile",
      image: "/screens/profile.PNG",
      thumbnail: "/screens/profile.PNG",
      features: ["Shows user profile Data", "Logout", "Edit Profile", "profile tabs"]
    },
    {
      id: 5,
      title: "Payments",
      description: "App users can reaceive payments through inapp payments",
      image: "/screens/payment.PNG",
      thumbnail: "/screens/payment.PNG",
      features: ["User gets paid", "Money is wired to the wallet", ]
    },
    {
      id: 6,
      title: "Wallet",
      description: "App users can reaceive payments through inapp payments",
      image: "/screens/wallet.PNG",
      thumbnail: "/screens/wallet.PNG",
      features: ["User withdraw their payment", "Money is wired to their Mobile Money Account", ]
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Household Manager, Dubai",
      text: "Home Manager transformed how we coordinate between our international staff. The multi-language support is incredible!",
      flag: "🇦🇪"
    },
    {
      name: "Miguel Rodriguez",
      role: "Estate Director, Madrid",
      text: "Finally, an app that understands the complexities of managing Homes, properties across different countries.",
      flag: "🇪🇸"
    },
    {
      name: "Aisha Bello",
      role: "Family Office Manager, Lagos",
      text: "The expense Smart Connection and reporting features have saved us countless hours each month. Highly recommended!",
      flag: "🇳🇬"
    }
  ];

  return (
    <div className="App">
      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }}></div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <div className="logo-orb">
              <span className="logo-icon">🏠</span>
            </div>
            <span className="logo-text">HomeManager</span>
          </div>
          
          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <a href="#home" className="nav-link">Home</a>
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <a href="#screenshots" className="nav-link">Screenshots</a>
            <a href="#values" className="nav-link">Values</a>
            <a href="#contact" className="nav-link">Contact</a>
            <a href="/terms" className="nav-link">Terms & Conditions</a>
          </div>

          <button 
            className="nav-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero section" ref={heroRef}>
        <div className="hero-background">
          <div className="floating-orb orb-1"></div>
          <div className="floating-orb orb-2"></div>
          <div className="floating-orb orb-3"></div>
        </div>
        
        <div className="hero-container">
          <div className="hero-content">
            <div className="badge">
              🏆 Trusted by households in 50+ countries
            </div>
            
            <h1 className="hero-title">
              <span className="hero-gradient">Home Management</span>
              <br />
              <span className="hero-sub">Perfected</span>
            </h1>
            
            <p className="hero-subtitle">
            Home Manager is a trusted Mobile platform that connects employers with reliable household staff in a simple, secure, and transparent way. Whether you’re looking to hire or seeking employment, Home Manager streamlines the process from search to payment.
            </p>

            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">50K+</div>
                <div className="stat-label">Active Homes</div>
              </div>
              <div className="stat">
                <div className="stat-number">15+</div>
                <div className="stat-label">Languages</div>
              </div>
              <div className="stat">
                <div className="stat-number">98%</div>
                <div className="stat-label">Satisfaction</div>
              </div>
            </div>
            
            <div className="hero-buttons">
              <button className="btn btn-primary">
                <span className="btn-icon">📱</span>
                Download on App Store
              </button>
              <button className="btn btn-secondary">
                <span className="btn-icon">🤖</span>
                Get it on Google Play
              </button>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="floating-device">
              <div className="device-mockup">
                <div className="device-screen">
                  <div className="app-demo">
                    <div className="demo-header">
                      <div className="demo-avatar"></div>
                      <div className="demo-user">
                        <div className="demo-name">Home Manager</div>
                        <div className="demo-status">● Active</div>
                      </div>
                    </div>
                    <div className="demo-stats">
                      <div className="demo-stat">
                        <span>Sent Requests</span>
                        <strong>15</strong>
                      </div>
                      <div className="demo-stat">
                        <span>Recieved Requests</span>
                        <strong>8</strong>
                      </div>
                    </div>
                    <div className="demo-stats">
                      <div className="demo-stat">
                        <span>Maids</span>
                        <strong>65</strong>
                      </div>
                      <div className="demo-stat">
                        <span>Cleaners</span>
                        <strong>48</strong>
                      </div>
                    </div>
                    <div className="demo-stats">
                      <div className="demo-stat">
                        <span>Drivers</span>
                        <strong>15</strong>
                      </div>
                      <div className="demo-stat">
                        <span>Nurses</span>
                        <strong>80</strong>
                      </div>
                    </div>
                    <div className="demo-stats">
                      <div className="demo-stat">
                        <span>Gardeners</span>
                        <strong>25</strong>
                      </div>
                      <div className="demo-stat">
                        <span>Personal Assistants</span>
                        <strong>800</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="recognition-section">
        <div className="container">
          <div className="recognition-grid">
          <div className="recognition-item">
              <span className="recognition-icon">🧽</span>
              <span>Nanny</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">🧹</span>
              <span>Cleaner</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">🚗</span>
              <span>Drivers</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">🎩</span>
              <span>Butlers</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">👮</span>
              <span>Security Personel</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">👨‍🍳</span>
              <span>Chef</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">📅</span>
              <span>Personal Assistant</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">🧽</span>
              <span>Maids</span>
            </div>
            <div className="recognition-item">
              <span className="recognition-icon">🌿</span>
              <span>Gardener</span>
            </div>

            <div className="recognition-item">
              <span className="recognition-icon">🛠️</span>
              <span>Handy Man</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="section features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Powerful Features</h2>
            <p className="section-subtitle">Everything you need to manage your home efficiently</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Get started in four simple steps</p>
          </div>
          
          <div className="steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="step-card" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="step-header">
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon">{step.icon}</div>
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                <div className="step-line"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Screenshots Section */}
      <section id="screenshots" className="section screenshots-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">App Experience</h2>
            <p className="section-subtitle">See how app users navigate through the App and Connect to best connections</p>
          </div>
          
          <div className="screenshots-container">
            {/* Main Display Area */}
            <div className="screenshot-display-area">
              <div className="screenshot-main">
                <div className="device-mockup-iphone">
                  <div className="device-frame">
                    <div className="device-screen">
                      <img 
                        src={screenshots[activeScreenshot].image} 
                        alt={screenshots[activeScreenshot].title}
                        className="screenshot-image"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/300/600';
                          e.target.alt = 'Screenshot preview';
                        }}
                      />
                    </div>
                  </div>
                  <div className="device-home-indicator"></div>
                </div>
              </div>
              
              {/* Screenshot Info */}
              <div className="screenshot-details">
                <div className="screenshot-meta">
                  <div className="screenshot-badge">Screen {activeScreenshot + 1} of {screenshots.length}</div>
                  <h3 className="screenshot-title">{screenshots[activeScreenshot].title}</h3>
                  <p className="screenshot-description">{screenshots[activeScreenshot].description}</p>
                </div>
                
                {/* Feature Highlights */}
                <div className="feature-highlights">
                  <h4 className="highlights-title">Key Features</h4>
                  <div className="highlights-grid">
                    {screenshots[activeScreenshot].features.map((feature, index) => (
                      <div key={index} className="highlight-item">
                        <div className="highlight-icon">✓</div>
                        <span className="highlight-text">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Thumbnails */}
            <div className="screenshots-navigation">
              <div className="navigation-header">
                <span className="navigation-title">All Screens</span>
                <div className="navigation-controls">
                  <button 
                    className="nav-arrow prev"
                    onClick={() => setActiveScreenshot(activeScreenshot > 0 ? activeScreenshot - 1 : screenshots.length - 1)}
                    aria-label="Previous screenshot"
                  >
                    ‹
                  </button>
                  <button 
                    className="nav-arrow next"
                    onClick={() => setActiveScreenshot(activeScreenshot < screenshots.length - 1 ? activeScreenshot + 1 : 0)}
                    aria-label="Next screenshot"
                  >
                    ›
                  </button>
                </div>
              </div>
              
              <div className="thumbnails-container">
                {screenshots.map((screenshot, index) => (
                  <div
                    key={screenshot.id}
                    className={`thumbnail-item ${activeScreenshot === index ? 'active' : ''}`}
                    onClick={() => setActiveScreenshot(index)}
                  >
                    <div className="thumbnail-frame">
                      <img 
                        src={screenshot.thumbnail || screenshot.image} 
                        alt={screenshot.title}
                        className="thumbnail-image"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/80/160';
                          e.target.alt = 'Thumbnail preview';
                        }}
                      />
                      <div className="thumbnail-overlay">
                        <div className="play-icon">▶</div>
                      </div>
                    </div>
                    <div className="thumbnail-label">
                      <span className="thumbnail-number">0{index + 1}</span>
                      <span className="thumbnail-title">{screenshot.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="section team-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Leadership Team</h2>
            <p className="section-subtitle">Meet the visionaries driving Home Manager's global success</p>
          </div>
          
          <div className="team-grid">
            {/* CEO - Center Position */}
            <div className="team-card ceo-card">
              <div className="team-card-inner">
                <div className="team-image-container">
                  <div className="image-frame">
                    <img 
                      src="/team/roseteam.png" 
                      alt="Nakalya Rose - CEO"
                      className="team-member-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="image-placeholder">
                      <span className="initials">NR</span>
                    </div>
                  </div>
                  <div className="role-badge">CEO</div>
                  <div className="decoration-ring"></div>
                </div>
                
                <div className="team-content">
                  <h3 className="team-name">Nakalya Rose</h3>
                  <p className="team-role">Chief Executive Officer</p>
                  <p className="team-bio">
                    Visionary leader driving Home Manager's global expansion and strategic direction 
                    with over 15 years of experience in technology innovation and business growth.
                  </p>
                  
                  <div className="team-stats">
                    <div className="stat">
                      <div className="stat-number">50+</div>
                      <div className="stat-label">Countries</div>
                    </div>
                    <div className="stat">
                      <div className="stat-number">15Y</div>
                      <div className="stat-label">Experience</div>
                    </div>
                  </div>
                  
                  <div className="team-social">
                    <a href="#" className="social-link" aria-label="LinkedIn">
                      <span className="social-icon">💼</span>
                    </a>
                    <a href="#" className="social-link" aria-label="Twitter">
                      <span className="social-icon">🐦</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* CTO */}
            <div className="team-card">
              <div className="team-card-inner">
                <div className="team-image-container">
                  <div className="image-frame">
                    <img 
                      src="/team/bannyprofile2.png" 
                      alt="Tumusiime Barnabus - CTO"
                      className="team-member-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="image-placeholder">
                      <span className="initials">TB</span>
                    </div>
                  </div>
                  <div className="role-badge">CTO</div>
                </div>
                
                <div className="team-content">
                  <h3 className="team-name">Tumusiime Barnabus</h3>
                  <p className="team-role">Chief Technology Officer</p>
                  <p className="team-bio">
                    Tech innovator leading our engineering vision with expertise in scalable 
                    architecture, AI/ML, and cutting-edge home automation technologies.
                  </p>
                  
                  <div className="expertise-tags">
                    <span className="expertise-tag">AI/ML</span>
                    <span className="expertise-tag">Cloud</span>
                    <span className="expertise-tag">Security</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Head of Marketing */}
            <div className="team-card">
              <div className="team-card-inner">
                <div className="team-image-container">
                  <div className="image-frame">
                    <img 
                      src="/team/roy.png" 
                      alt="Roy Godwin Karugaba - Head of Marketing"
                      className="team-member-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="image-placeholder">
                      <span className="initials">RGK</span>
                    </div>
                  </div>
                  <div className="role-badge">Marketing</div>
                </div>
                
                <div className="team-content">
                  <h3 className="team-name">Roy Godwin Karugaba</h3>
                  <p className="team-role">Head of Marketing</p>
                  <p className="team-bio">
                    Growth strategist expanding Home Manager's global footprint through 
                    innovative marketing campaigns and strategic international partnerships.
                  </p>
                  
                  <div className="team-metrics">
                    <div className="metric">
                      <span className="metric-value">300%</span>
                      <span className="metric-label">Growth</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">10k+</span>
                      <span className="metric-label">Users</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CFO */}
            <div className="team-card">
              <div className="team-card-inner">
                <div className="team-image-container">
                  <div className="image-frame">
                    <img 
                      src="/team/lakiza.png" 
                      alt="Karungi Claire - CFO"
                      className="team-member-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="image-placeholder">
                      <span className="initials">KC</span>
                    </div>
                  </div>
                  <div className="role-badge">CFO</div>
                </div>
                
                <div className="team-content">
                  <h3 className="team-name">Karungi Claire</h3>
                  <p className="team-role">Chief Financial Officer</p>
                  <p className="team-bio">
                    Financial strategist ensuring sustainable growth and operational excellence 
                    through meticulous financial planning, analysis, and investor relations.
                  </p>
                  
                  <div className="achievement-badge">
                    <span className="badge-icon">💰</span>
                    <span className="badge-text">Series B Funded</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Company Stats */}
          <div className="company-stats">
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-icon">🌍</div>
                <div className="stat-content">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Countries Served</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">🏠</div>
                <div className="stat-content">
                  <div className="stat-number">50K+</div>
                  <div className="stat-label">Active Homes</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">⭐</div>
                <div className="stat-content">
                  <div className="stat-number">4.9</div>
                  <div className="stat-label">App Store Rating</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">🚀</div>
                <div className="stat-content">
                  <div className="stat-number">2020</div>
                  <div className="stat-label">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="section testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Users Say</h2>
            <p className="section-subtitle">Trusted by households worldwide</p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-flag">{testimonial.flag}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section id="values" className="section values-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Values</h2>
            <p className="section-subtitle">What drives everything we do</p>
          </div>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="value-icon">
                  {value.icon}
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Home Management?</h2>
            <p className="cta-subtitle">Join thousands of satisfied households worldwide</p>
            <div className="cta-buttons">
              <button className="btn btn-primary">
                <span className="btn-icon">🚀</span>
                Download App
              </button>
              <button className="btn btn-secondary">
                <span className="btn-icon">📞</span>
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <div className="logo-orb small">
                  <span className="logo-icon">🏠</span>
                </div>
                <span className="logo-text">HomeManager</span>
              </div>
              <p className="footer-description">
                The intelligent home management platform trusted by households worldwide.
              </p>
              <div className="social-links">
                {['📘', '🐦', '📷', '💼', '👔'].map((icon, index) => (
                  <a key={index} href="#" className="social-link">
                    {icon}
                  </a>
                ))}
              </div>
            </div>
            
            <div className="footer-section">
              <h4>Product</h4>
              <a href="#">Features</a>
              <a href="#">Pricing</a>
              <a href="#">Case Studies</a>
              <a href="#">API</a>
            </div>

            <div className="footer-section">
              <h4>Company</h4>
              <a href="#">About Us</a>
              <a href="#">Team</a>
              <a href="#">Careers</a>
              <a href="#">Contact</a>
            </div>

            <div className="footer-section">
              <h4>Support</h4>
              <a href="#">Help Center</a>
              <a href="#">FAQs</a>
              <a href="#">Community</a>
              <a href="#">Status</a>
            </div>

            <div className="footer-section">
              <h4>Legal</h4>
              <Link to="/terms">Terms & Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
             
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2026 HomeManager by Xigen Technologies. All rights reserved.</p>
            <div className="footer-links">
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
              
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
    </Router>
  );
}

export default App;